import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useCredentialedDataProviderOrgs from '@/hooks/useCredentialedDataProviderOrgs'
import { OverlayState } from '@/hooks/useOverlay'
import usePositionStartingPoints from '@/hooks/usePositionStartingPoints'
import {
  useCreatePosition,
  useUpdatePosition,
} from '@/service-library/hooks/positions'
import { PositionLookup } from '@/types/position-lookups'
import { Position } from '@/types/positions'
import {
  emptyParams,
  findLookup,
  hasEveryRequiredParam,
  isNameTaken,
  paramsAfterChange,
} from '@/utils/positions'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import FormRadioGroup from '@/components/forms/FormRadioGroup'
import { useOrganizationsContext } from '@/components/organizations/OrganizationsProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import PositionParamField from './PositionParamField'

type PositionEditorDialogProps = {
  overlay: OverlayState
  /** Omit to add a new position. */
  position?: Position
  lookups: PositionLookup[]
  /** Names already used by the project's other positions. */
  takenNames: string[]
  listQueryKey: QueryKey
  /**
   * Called once the dialog has finished closing. The form is filled in from
   * `position` when this mounts, so the caller has to unmount it here or the
   * next open still shows the last position's values.
   */
  onExited: () => void
}

/** These are the fields a save sends, not the controls on screen. */
type PositionFormValues = {
  name: string
  lookup_code: string
  starting_point_info_type_id: string | null
  params: Record<string, string>
}

type OptionCardProps = {
  value: string
  title: string
  description: string
  selected: boolean
}

function OptionCard({ value, title, description, selected }: OptionCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
      }}
    >
      <FormControlLabel
        value={value}
        control={<Radio />}
        sx={{ m: 0, width: '100%', alignItems: 'flex-start', px: 1.5, py: 1 }}
        label={
          <Stack spacing={0.25} sx={{ pt: 0.75 }}>
            <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Stack>
        }
      />
    </Paper>
  )
}

export default function PositionEditorDialog({
  overlay,
  position,
  lookups,
  takenNames,
  listQueryKey,
  onExited,
}: PositionEditorDialogProps) {
  const { project } = useProjectContext()

  const methods = useForm<PositionFormValues>({
    defaultValues: {
      name: position?.name ?? '',
      lookup_code: position?.lookup_code ?? lookups[0]?.key ?? '',
      starting_point_info_type_id:
        position?.starting_point_info_type_id ?? null,
      params: position?.params ?? {},
    },
  })
  const { formState, setValue, watch } = methods
  const { isValid } = formState

  const name = watch('name')
  const lookupCode = watch('lookup_code')
  const startingPointInfoTypeId = watch('starting_point_info_type_id')
  const params = watch('params')

  // Live choices are read through a company that can reach a login for the
  // project's accounting system. The relay only looks upwards for that login,
  // so the project's own org is the fallback rather than the first choice: in
  // our own test and demo projects the login sits on a company below it.
  const { organizations } = useOrganizationsContext()
  const dataProviderOrgs = useMemo(
    () => organizations.filter((org) => org.is_relay_data_provider),
    [organizations],
  )
  const { credentialedDataProviderOrgs } = useCredentialedDataProviderOrgs({
    project,
    dataProviderOrgs,
  })
  const optionsOrgId = credentialedDataProviderOrgs[0]?.id ?? project.org_id

  const lookup = findLookup(lookups, lookupCode)
  const {
    startingPoints,
    isPending: startingPointsPending,
    isError: startingPointsError,
  } = usePositionStartingPoints(lookup)

  // Most positions start from the only thing on offer, and the ones that
  // don't are a change away. Picking the first leaves nothing to fill in
  // before a position can be saved.
  const firstStartingPointId = startingPoints[0]?.infoTypeId
  useEffect(() => {
    // Only when nothing is chosen. A saved position keeps what it has, even
    // where the project no longer offers it.
    if (startingPointInfoTypeId || !firstStartingPointId) return
    setValue('starting_point_info_type_id', firstStartingPointId, {
      shouldValidate: true,
    })
  }, [firstStartingPointId, setValue, startingPointInfoTypeId])

  const { createPosition, isPending: isCreating } = useCreatePosition({
    listQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Position Created')
      overlay.close()
    },
    onError: () => {
      showErrorSnackbar('Failed to create position. Please try again later.')
    },
  })

  const { updatePosition, isPending: isUpdating } = useUpdatePosition({
    listQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Position Updated')
      overlay.close()
    },
    onError: () => {
      showErrorSnackbar('Failed to update position. Please try again later.')
    },
  })

  const isSaving = isCreating || isUpdating
  // Saving adds this position to the list before the request comes back, so
  // from that moment its own name reads as taken. Nothing typed can change
  // once a save is under way, so stop checking then — and once the dialog is
  // closing, which it stays mounted through.
  // Positions saved under different companies could share a name, so one
  // keeping the name it already has is never a duplicate.
  const keepsSavedName = !!position && isNameTaken(name, [position.name])
  const isDuplicateName =
    !isSaving &&
    overlay.isOpen &&
    !keepsSavedName &&
    isNameTaken(name, takenNames)

  // `isValid` covers the name. The params are a single form value holding a
  // set the accounting system decides, so which of them are required is asked
  // of it instead. The starting point is checked here rather than left to the
  // form, because the control carrying its rule is not on screen at all when
  // this project has no field to offer — and a lookup that needs one saves a
  // position that can never find anybody without it.
  const canSave =
    isValid &&
    !isDuplicateName &&
    !!lookup &&
    hasEveryRequiredParam(lookup, params) &&
    (!lookup.scope.required || !!startingPointInfoTypeId)

  const handleLookupChange = (nextLookupCode: string) => {
    const nextLookup = findLookup(lookups, nextLookupCode)
    if (!nextLookup) return

    setValue('params', emptyParams(nextLookup))

    // A starting point only means something to a lookup that accepts it.
    const keepsStartingPoint =
      nextLookup.scope.required &&
      !!startingPointInfoTypeId &&
      nextLookup.scope.accepts.includes(startingPointInfoTypeId)
    setValue(
      'starting_point_info_type_id',
      keepsStartingPoint ? startingPointInfoTypeId : null,
      { shouldValidate: true },
    )
  }

  const handleParamChange = (paramName: string, value: string) => {
    if (!lookup) return
    const next = { ...params, [paramName]: value }
    setValue('params', paramsAfterChange(lookup, next, paramName))
  }

  const handleSave = (values: PositionFormValues) => {
    if (!canSave || isSaving) return

    const payload = {
      name: values.name.trim(),
      lookup_code: values.lookup_code,
      starting_point_info_type_id: values.starting_point_info_type_id,
      params: values.params,
    }

    if (position) {
      updatePosition({ ...position, ...payload })
    } else {
      createPosition({
        id: generateUuid(),
        project_id: project.id,
        dp_org_id: null,
        description: '',
        ...payload,
      })
    }
  }

  return (
    <Dialog
      {...overlay}
      maxWidth="sm"
      TransitionProps={{ onExited }}
      title={position ? `Edit ${position.name}` : 'Add position'}
      subtitle={
        position
          ? undefined
          : 'Positions resolve to real people from your ERP. The settings below decide how.'
      }
    >
      <PixyDocsForm id="position-form" methods={methods} onSubmit={handleSave}>
        <DialogContent>
          <Stack spacing={3}>
            <FormTextField
              name="name"
              label="Your name for this position"
              placeholder="e.g. ESH Director"
              fullWidth
              required
              error={isDuplicateName}
              helperText={
                isDuplicateName
                  ? `This project already has a position called “${name.trim()}”. Edit that one instead — two positions with the same name can't be told apart wherever one is used.`
                  : 'What Pixydocs calls this position. It does not have to match what your ERP calls it.'
              }
              useControllerProps={{
                rules: { validate: (value) => value.trim().length > 0 },
              }}
            />

            <Stack spacing={1}>
              <Typography variant="subtitle2">
                How its holders are found
              </Typography>
              <FormRadioGroup
                name="lookup_code"
                fullWidth
                radioGroupProps={{
                  'aria-label': 'How its holders are found',
                  onChange: (_event, next) => handleLookupChange(next),
                }}
              >
                <Stack spacing={1}>
                  {lookups.map((option) => (
                    <OptionCard
                      key={option.key}
                      value={option.key}
                      title={option.name}
                      description={option.description}
                      selected={option.key === lookupCode}
                    />
                  ))}
                </Stack>
              </FormRadioGroup>
              {lookup && (
                <Typography variant="caption" color="text.secondary">
                  {lookup.scope.required
                    ? 'This resolves once for each distinct value on the document. A person who fills multiple positions only needs to approve once.'
                    : 'This resolves once, to whoever currently holds it. Any one of them counts, however many there are.'}
                </Typography>
              )}
            </Stack>

            {lookup?.scope.required && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">Starts from</Typography>
                {startingPointsPending && <CircularProgress size={18} />}

                {startingPointsError && (
                  <Alert severity="warning" variant="outlined">
                    Couldn&apos;t read this project&apos;s fields, so the
                    starting points can&apos;t be listed.
                  </Alert>
                )}

                {!startingPointsPending &&
                  !startingPointsError &&
                  startingPoints.length === 0 && (
                    <Alert severity="warning" variant="outlined">
                      No field in this project carries the information this
                      method needs. Add one to the project&apos;s fields first.
                    </Alert>
                  )}

                {startingPoints.length > 0 && (
                  <FormRadioGroup
                    name="starting_point_info_type_id"
                    fullWidth
                    radioGroupProps={{ 'aria-label': 'Starts from' }}
                    useControllerProps={{
                      rules: {
                        validate: (value) => !lookup.scope.required || !!value,
                      },
                    }}
                  >
                    <Stack spacing={1}>
                      {startingPoints.map((startingPoint) => (
                        <OptionCard
                          key={startingPoint.infoTypeId}
                          value={startingPoint.infoTypeId}
                          title={startingPoint.label}
                          description={startingPoint.fieldNames.join(' · ')}
                          selected={
                            startingPoint.infoTypeId === startingPointInfoTypeId
                          }
                        />
                      ))}
                    </Stack>
                  </FormRadioGroup>
                )}
              </Stack>
            )}

            {lookup?.params.map((param) => (
              <PositionParamField
                // The lookup is part of the key because a param of the same
                // name can take typed text under one lookup and only a listed
                // choice under another. Without it React reuses the same input
                // and flips it between controlled and uncontrolled, which
                // throws.
                key={`${lookup.key}:${param.name}`}
                lookup={lookup}
                param={param}
                params={params}
                // A position saved under a company resolves against that
                // company's books, so its choices have to come from there too.
                orgId={position?.dp_org_id ?? optionsOrgId}
                orgCredentialTypeId={project.org_credential_type_id || ''}
                onChange={(value) => handleParamChange(param.name, value)}
              />
            ))}
          </Stack>
        </DialogContent>

        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSaving}
            disabled={!canSave}
          >
            {position ? 'Save position' : 'Add position'}
          </LoadingButton>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
